import type { SupportedLocale } from "@safesphere/contracts";

import { loadBackupMetadata } from "../family/backup.js";
import { hasVault, isVaultUnlocked, loadQuickPlan } from "../family/familyRepository.js";
import { fictionalPlanId } from "../family/fictionalFixtures.js";
import { determineContentAvailability } from "../offline/contentState.js";
import { getBundleStorageSnapshot } from "../offline/database.js";

export const readinessStates = ["READY", "NEEDS_ATTENTION", "UNAVAILABLE", "OPTIONAL"] as const;
export type ReadinessState = (typeof readinessStates)[number];

export interface ReadinessSignal {
  readonly id:
    | "APP_SHELL"
    | "SERVICE_WORKER"
    | "ACTIVE_CONTENT"
    | "KNOWN_GOOD"
    | "LOCALE_CONTENT"
    | "CONTENT_FRESHNESS"
    | "FAMILY_PLAN"
    | "VAULT"
    | "BACKUP"
    | "QUICK_ACCESS";
  readonly state: ReadinessState;
  readonly detail?: string;
}

export interface ReadinessSnapshot {
  readonly signals: readonly ReadinessSignal[];
  readonly backupExportedAt?: string;
}

export interface ReadinessEnvironment {
  readonly appShellAvailableOffline: boolean;
  readonly serviceWorkerAvailable: boolean;
}

export async function loadReadinessSnapshot(
  locale: SupportedLocale,
  environment: ReadinessEnvironment = {
    appShellAvailableOffline: true,
    serviceWorkerAvailable:
      "serviceWorker" in navigator && navigator.serviceWorker.controller !== null,
  },
): Promise<ReadinessSnapshot> {
  const [storage, contentState, quick, vaultExists, metadata] = await Promise.all([
    getBundleStorageSnapshot(locale),
    determineContentAvailability(locale),
    loadQuickPlan(fictionalPlanId),
    hasVault(fictionalPlanId),
    loadBackupMetadata(),
  ]);
  const quickReady =
    quick !== undefined &&
    quick.contacts.length > 0 &&
    quick.meetingPoints.length > 0 &&
    quick.checklists.length > 0;
  const contentReady = contentState === "READY" || contentState === "STALE_USABLE";
  const freshness: ReadinessState =
    contentState === "READY"
      ? "READY"
      : contentState === "STALE_USABLE"
        ? "NEEDS_ATTENTION"
        : "UNAVAILABLE";

  return {
    ...(metadata === undefined ? {} : { backupExportedAt: metadata.exportedAt }),
    signals: [
      { id: "APP_SHELL", state: environment.appShellAvailableOffline ? "READY" : "UNAVAILABLE" },
      { id: "SERVICE_WORKER", state: environment.serviceWorkerAvailable ? "READY" : "OPTIONAL" },
      { id: "ACTIVE_CONTENT", state: contentReady ? "READY" : "NEEDS_ATTENTION" },
      {
        id: "KNOWN_GOOD",
        state: storage.knownGoodBundle === undefined ? "NEEDS_ATTENTION" : "READY",
      },
      { id: "LOCALE_CONTENT", state: storage.activeBundle === undefined ? "UNAVAILABLE" : "READY" },
      { id: "CONTENT_FRESHNESS", state: freshness },
      { id: "FAMILY_PLAN", state: quick === undefined ? "UNAVAILABLE" : "READY" },
      {
        id: "VAULT",
        state: vaultExists ? (isVaultUnlocked(fictionalPlanId) ? "READY" : "OPTIONAL") : "OPTIONAL",
      },
      { id: "BACKUP", state: metadata === undefined ? "NEEDS_ATTENTION" : "READY" },
      {
        id: "QUICK_ACCESS",
        state: quickReady ? "READY" : quick === undefined ? "UNAVAILABLE" : "NEEDS_ATTENTION",
      },
    ],
  };
}
